import { useSyncExternalStore } from 'react';

/** The timesheet entry produced by the most recent timer commit. `seq`
 *  increments on every signal so the timesheet page can tell a fresh commit
 *  from a stale one it has already handled (two commits of distinct timers
 *  yield distinct entry ids, but the counter also guards re-mounts and the
 *  unlikely repeat). */
export interface CommittedEntryFocus {
  entryId: string;
  date: string;
  seq: number;
}

let current: CommittedEntryFocus | null = null;
let seq = 0;
const listeners = new Set<() => void>();

/**
 * Announce that a timer was committed into `entry`. The global timer popover
 * lives outside the timesheet page's React tree, so this module-level store is
 * the bridge: the page subscribes via {@link useCommittedEntryFocus} and
 * scrolls the produced row into view. Carrying only `id` + `date` keeps the
 * signal independent of the full `Timesheet` client type.
 */
export function signalCommittedEntry(entry: { id: string; date: string }): void {
  seq += 1;
  current = { entryId: entry.id, date: entry.date, seq };
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** The current snapshot — read directly (e.g. to baseline the seq at mount). */
export function getCommittedEntryFocus(): CommittedEntryFocus | null {
  return current;
}

/** Subscribe a component to timer-commit signals. The server snapshot is
 *  always `null` — this is a client-only ephemeral signal. */
export function useCommittedEntryFocus(): CommittedEntryFocus | null {
  return useSyncExternalStore(subscribe, getCommittedEntryFocus, () => null);
}
